import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useMedia } from "react-use";
import { Space, Typography } from "antd";
import { EmailIcon, PhoneIcon, UserAddressIcon, UserIcon } from "assets/icons";
import { getIn, useFormik } from "formik";
import { AuthInfo } from "stores/slices/auth";
import { setHeaderTitle } from "stores/slices/layout";
import { getWorkspaceInfo } from "stores/slices/workspace";

import AppButton from "components/button";
import FormInput from "components/forms/components/formInput";

import WorkspaceProfileForm from "./forms/workspaceProfileForm";

import styles from "../styles.module.scss";

const WorkspaceProfilePage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const authInfo = useSelector(AuthInfo);
  const [isOpenEdit, setIsOpenEdit] = useState(false);
  const [workspaceInfo, setWorkspaceInfo] = useState(null);
  const below768 = useMedia("(max-width: 767.2px)");

  const handleForm = useFormik({
    initialValues: {
      workspaceName: workspaceInfo?.name || "-",
      address: workspaceInfo?.address || "-",
      contactName: workspaceInfo?.contactName || "-",
      contactNumber: workspaceInfo?.phoneNumber || "-",
      contactEmail: workspaceInfo?.contactEmail || "-",
      contactInvoices: workspaceInfo?.invoiceEmail || "-",
    },
  });
  const { values, setFieldValue, setValues } = handleForm;

  const inputItems = [
    {
      type: "text",
      key: "workspaceName",
      label: t("label.workspaceName"),
      placeholder: "label.workspaceName",
      icon: <UserIcon />,
      span: 24,
      style: {
        marginBottom: 0,
        paddingTop: below768 ? 10 : 0,
        paddingBottom: below768 ? 10 : 0,
        borderBottom: "0.5px solid #F4F5F7",
      },
      readOnly: true,
    },
    {
      type: "text",
      key: "address",
      label: t("workspace.companyAddress"),
      placeholder: "workspace.companyAddress",
      icon: <UserAddressIcon />,
      span: 24,
      style: {
        marginBottom: 0,
        paddingBottom: below768 ? 10 : 0,
        borderBottom: "0.5px solid #F4F5F7",
      },
      readOnly: true,
      config: {
        multipleLine: true,
      },
    },
    {
      type: "text",
      key: "contactName",
      label: t("label.contactName"),
      placeholder: "label.contactName",
      icon: <UserIcon />,
      value: "davesmith@jimmowing.com",
      span: 24,
      style: {
        marginBottom: 0,
        paddingBottom: below768 ? 10 : 0,
        borderBottom: "0.5px solid #F4F5F7",
      },
      readOnly: true,
    },
    {
      type: "text",
      key: "contactNumber",
      label: t("label.contactNumber"),
      placeholder: "label.contactNumber",
      icon: <PhoneIcon />,
      value: "Smith",
      span: 24,
      style: {
        marginBottom: 0,
        paddingBottom: below768 ? 10 : 0,
        borderBottom: "0.5px solid #F4F5F7",
      },
      readOnly: true,
    },
    {
      type: "text",
      key: "contactEmail",
      label: t("label.contactEmail"),
      placeholder: "label.contactEmail",
      icon: <EmailIcon />,
      span: 24,
      style: {
        marginBottom: 0,
        paddingBottom: below768 ? 10 : 0,
        borderBottom: "0.5px solid #F4F5F7",
      },
      readOnly: true,
    },
    {
      type: "text",
      key: "contactInvoices",
      label: t("label.contactInvoices"),
      placeholder: "label.contactInvoices",
      icon: <EmailIcon />,
      span: 24,
      style: {
        marginBottom: 8,
        paddingBottom: below768 ? 10 : 0,
      },
      readOnly: true,
    },
  ];

  useEffect(() => {
    dispatch(setHeaderTitle("workspaceProfile"));
    if (authInfo && authInfo.workspaceId) {
      loadData().then(r => setWorkspaceInfo(r));
    }
  }, [authInfo]);

  const loadData = async () => {
    const { payload } = await dispatch(getWorkspaceInfo(authInfo.workspaceId));
    return payload;
  };

  useEffect(() => {
    if (workspaceInfo) {
      setValues({
        ...workspaceInfo,
        workspaceName: workspaceInfo.name || "-",
        address: workspaceInfo.address || "-",
        contactName: workspaceInfo.contactName || "-",
        contactNumber: workspaceInfo.phoneNumber || "-",
        contactEmail: workspaceInfo.contactEmail || "-",
        contactInvoices: workspaceInfo.invoiceEmail || "-",
      });
    }
  }, [workspaceInfo]);

  return (
    <div className={styles.profilePage}>
      <div className={styles.profileHeader}>
        <Typography
          style={{
            fontSize: "36px",
            fontWeight: 500,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            width: 800,
            maxWidth: "100%",
          }}
        >
          {workspaceInfo && workspaceInfo.name}
        </Typography>
      </div>
      <div className={styles.profileContent}>
        <div>
          <Typography
            style={{
              fontSize: "20px",
              fontWeight: 500,
              borderBottom: "3px solid #5162F3",
              color: "#5162F3",
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "center",
              marginBottom: "16px",
            }}
          >
            {t("label.workspaceDetails")}
          </Typography>
        </div>
        <Space
          direction="vertical"
          size="small"
          style={{
            display: "flex",
          }}
        >
          <FormInput
            items={inputItems.map(item => {
              item.label = t(item.label);
              if (item.key) {
                item.value = getIn(values, item.key);
              }
              return { ...item };
            })}
            form={handleForm}
            onChange={setFieldValue}
          />
          <div className={styles.customButtonRight}>
            <div className={styles.buttonRight}>
              <AppButton
                label={t("label.edit")}
                color={"primary"}
                type="submit"
                onClick={() => setIsOpenEdit(true)}
              />
            </div>
          </div>
        </Space>
      </div>

      <WorkspaceProfileForm
        isOpenEdit={isOpenEdit}
        setIsOpenEdit={setIsOpenEdit}
      />
    </div>
  );
};

export default WorkspaceProfilePage;
